/**
 * Backs the NAV DISCOVERY MENU (`CirclesNavMenu` — the caret dropdown offering
 * circles to zero-circle cohort viewers) with the two Brooklyn circle-pool
 * operations the onboarding step also uses: `circlesForOnboarding` (the
 * viewer's joinable rooms — beta-2: one option per open circle, ranked
 * per-viewer) and `assignToCircle` (join the chosen room by its circle key).
 *
 * Deliberately a SIBLING of `useOnboardingCircles`, not a reuse of it: that
 * hook adds a settle-before-render retry policy (`OFFER_RETRY_ATTEMPTS`,
 * `OFFER_PENDING_TIMEOUT_MS`, the `OFFER_PENDING` sentinel reported through
 * `error`) built for onboarding's race against the un-awaited
 * `/v5/user/onboard` POST. The nav menu renders on every page long after
 * onboarding — it has no such race, and the sentinel protocol would read as
 * "unknown" to its gate. What the two MUST share stays shared: the gql
 * documents and the `toOnboardingOption` adapter (the anti-corruption
 * boundary), so a schema change — e.g. the anticipated
 * `OnboardingCircleOption.description` field — lands in exactly one mapping.
 *
 * Community rules: Apollo for data; `cache-and-network` + `errorPolicy: 'all'`
 * for the user-scoped read (a PARTIAL error must arrive alongside usable
 * data — the caller decides what an error means for its gate); the mutation
 * awaited in try/catch, staying in the current state on failure. `jwt`/`skip`
 * are threaded in from the component boundary — the hook never reads
 * selectors itself.
 */
import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  APOLLO_CLIENT_ERROR_POLICY_ALL,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
} from '../../../apollo-graphql/index'
import type {
  AssignCircleErrorKind,
  OnboardingCircleOption,
} from '../../../../components/Community/Circles/shared/types'
import { ASSIGN_TO_CIRCLE, CIRCLES_FOR_ONBOARDING } from '../gql/query'
import type {
  AssignToCircleResponse,
  AssignToCircleVariables,
  CirclesForOnboardingResponse,
} from '../gql/types'
import { toOnboardingOption } from '../gql/adapters'
import { classifyAssignError } from './assignErrorClassifier'

/**
 * The typed outcome of a nav-menu join (CO-515 beta 2). Success carries the
 * joined circle's feed id; failure carries a null id plus a classified
 * `AssignCircleErrorKind` so the caller can pick per-kind feedback copy.
 */
export type NavMenuJoinResult =
  | { feedId: string }
  | { feedId: null, error: AssignCircleErrorKind }

export interface UseNavMenuOfferParams {
  jwt: string
  /**
   * Gate the query off entirely. The cohort/membership/opt-out math lives with
   * the caller (`useCirclesNavMenu`) — this hook only fetches and maps.
   */
  skip: boolean
}

export interface UseNavMenuOfferResult {
  /** Adapter-mapped offer options (`[]` until data arrives). */
  options: OnboardingCircleOption[]
  /**
   * True only during the FIRST fetch (`loading && !data`, the slice-hook
   * convention) — cache-and-network revalidations never re-flag it.
   */
  loading: boolean
  /**
   * Resolves a typed result: `{ feedId }` with the joined circle's feed id on
   * success, or `{ feedId: null, error }` with a classified kind on failure —
   * including the resolved-but-null shape `useOnboardingCircles` also defends
   * against (the mutation reported no error yet handed back nothing to join →
   * `generic`). Never throws; the caller keys retryability off `feedId: null`
   * and picks its toast copy off `error`.
   */
  join: (rainbowCategoryKey: number) => Promise<NavMenuJoinResult>
  /**
   * Re-issue the offer query (rejections swallowed). Recovery for the
   * fail-closed session blank — see the comment at the definition.
   */
  refetchOffer: () => void
  /**
   * The query settled in error with NO usable data (partial errors alongside
   * data do NOT flag this — errorPolicy 'all' delivers those as data+error and
   * the menu renders). While true the menu is invisible for the rest of the
   * SPA session unless `refetchOffer` recovers it.
   */
  offerErrored: boolean
}

export default function useNavMenuOffer (
  { jwt, skip }: UseNavMenuOfferParams,
): UseNavMenuOfferResult {
  const {
    data, loading, error, refetch,
  } = useQuery<CirclesForOnboardingResponse>(
    CIRCLES_FOR_ONBOARDING,
    {
      skip,
      context: { jwt },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    },
  )

  // Recovery hook for the fail-closed session blank: the menu hides entirely
  // when this query settles in error with no data, and nothing else ever
  // re-issues it (the app-wide RetryLink covers only fast-recovering 5xx —
  // never network-level failures, and only within its ~seconds budget). The
  // caller re-fires this on connectivity returning. Swallow rejections — a
  // failed retry just leaves the existing fail-closed state.
  const refetchOffer = useCallback(() => { refetch().catch(() => {}) }, [refetch])

  const options = useMemo<OnboardingCircleOption[]>(() => (
    (data?.circlesForOnboarding ?? []).map(toOnboardingOption)
  ), [data])

  const [assignMutation] = useMutation<AssignToCircleResponse, AssignToCircleVariables>(
    ASSIGN_TO_CIRCLE,
    { context: { jwt } },
  )

  const join = useCallback(async (rainbowCategoryKey: number): Promise<NavMenuJoinResult> => {
    try {
      const { data: result } = await assignMutation({ variables: { rainbowCategoryKey } })
      const feedId = result?.assignToCircle?.id
      // A resolved-but-null feed means the mutation reported no error yet
      // handed back nothing to join — a failure, not a join.
      if (!feedId) return { feedId: null, error: 'generic' }
      return { feedId }
    } catch (err) {
      // Stay in the current state on failure (Community rule 8), classifying
      // the failure so the caller can pick per-kind feedback copy.
      return { feedId: null, error: classifyAssignError(err) }
    }
  }, [assignMutation])

  return {
    options,
    loading: loading && !data,
    join,
    refetchOffer,
    offerErrored: !!error && !data,
  }
}
